import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Building2, Send, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.1, ease: "easeOut" } }),
};

const initialForm = { name: "", company: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.message) {
      toast.error("Udfyld venligst navn, virksomhed, e-mail og besked.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm(initialForm);
      toast.success("Tak for din henvendelse! Vi vender tilbage hurtigst muligt.");
    } catch (err) {
      toast.error("Noget gik galt. Prøv igen, eller send os en e-mail direkte.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <span className="text-sm font-semibold uppercase tracking-widest text-brand">Kontakt</span>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            Kom i kontakt med SB Logistik
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            Har du spørgsmål eller ønsker et uforpligtende tilbud? Udfyld
            formularen nedenfor eller send os en e-mail – vi vender tilbage
            hurtigst muligt.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-14 px-6 py-20 lg:grid-cols-[1fr_1.2fr]">
        {/* Contact info */}
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Kontaktoplysninger</h2>
          <div className="mt-8 space-y-6">
            <div className="flex items-start gap-4 rounded-sm border border-slate-200 bg-white p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-brand/10 text-brand">
                <Building2 className="h-5 w-5" />
              </span>
              <div>
                <div className="font-heading font-semibold text-slate-900">SB Logistik</div>
                <div data-testid="contact-cvr" className="text-sm text-slate-600">CVR: 46603206</div>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-sm border border-slate-200 bg-white p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-brand/10 text-brand">
                <MapPin className="h-5 w-5" />
              </span>
              <div>
                <div className="font-heading font-semibold text-slate-900">Adresse</div>
                <div data-testid="contact-address" className="text-sm leading-relaxed text-slate-600">
                  Kløvervej 144, 3. th<br />6100 Haderslev
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-sm border border-slate-200 bg-white p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-brand/10 text-brand">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <div className="font-heading font-semibold text-slate-900">Telefon</div>
                <div data-testid="contact-phone" className="text-sm text-slate-600">+45 31 81 80 44</div>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-sm border border-slate-200 bg-white p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-brand/10 text-brand">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <div className="font-heading font-semibold text-slate-900">E-mail</div>
                <a
                  data-testid="contact-email-link"
                  href="mailto:info@sb-logistik.dk"
                  className="text-sm font-medium text-brand transition-colors hover:text-brand-hover"
                >
                  info@sb-logistik.dk
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Form */}
        <motion.div
          initial="hidden" animate="show" custom={1} variants={fadeUp}
          className="rounded-sm border border-slate-200 bg-white p-8 md:p-10"
        >
          {sent ? (
            <div data-testid="contact-success" className="flex flex-col items-center justify-center py-16 text-center">
              <CheckCircle2 className="h-16 w-16 text-brand" />
              <h3 className="mt-6 font-heading text-2xl font-semibold text-slate-900">Tak for din henvendelse!</h3>
              <p className="mt-3 max-w-sm text-slate-600">
                Vi har modtaget din besked og vender tilbage hurtigst muligt.
              </p>
              <button
                data-testid="contact-reset-button"
                onClick={() => setSent(false)}
                className="mt-8 rounded-sm bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
              >
                Send en ny besked
              </button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Navn *</Label>
                  <Input data-testid="contact-name-input" id="name" name="name" value={form.name} onChange={handleChange} placeholder="Dit navn" className="focus-visible:ring-brand" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Virksomhed *</Label>
                  <Input data-testid="contact-company-input" id="company" name="company" value={form.company} onChange={handleChange} placeholder="Din virksomhed" className="focus-visible:ring-brand" />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail *</Label>
                  <Input data-testid="contact-email-input" id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="din@email.dk" className="focus-visible:ring-brand" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefonnummer <span className="text-slate-400">(valgfrit)</span></Label>
                  <Input data-testid="contact-phone-input" id="phone" name="phone" value={form.phone} onChange={handleChange} placeholder="+45 ..." className="focus-visible:ring-brand" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Besked *</Label>
                <Textarea data-testid="contact-message-input" id="message" name="message" value={form.message} onChange={handleChange} rows={6} placeholder="Fortæl os om din transportopgave eller dit spørgsmål ..." className="focus-visible:ring-brand" />
              </div>

              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-sm bg-brand px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-brand-hover disabled:opacity-60 sm:w-auto"
              >
                {loading ? "Sender ..." : <>Send besked <Send className="h-5 w-5" /></>}
              </button>

              <p className="pt-2 text-sm text-slate-500">
                Du kan også skrive direkte til os på{" "}
                <a data-testid="form-email-link" href="mailto:info@sb-logistik.dk" className="font-medium text-brand hover:text-brand-hover">
                  info@sb-logistik.dk
                </a>
              </p>
            </form>
          )}
        </motion.div>
      </section>
    </div>
  );
}
